/**
 * DOCKHTA - WebSocket Hook for Real-time Chat
 * Provides WebSocket connection management with auto-reconnect
 * Fixed: Uses refs for callbacks to prevent React 18 StrictMode double-mount reconnections
 */

import { useState, useEffect, useRef, useCallback } from 'react';

const WS_RECONNECT_DELAY = 3000;
const WS_PING_INTERVAL = 30000;

export const useWebSocket = (conversationId, token, onMessage, onPresence, onTyping) => {
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  
  // Use refs for WebSocket and timers
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const mountedRef = useRef(true);
  const connectingRef = useRef(false);
  
  // Store callbacks in refs to avoid dependency changes
  const onMessageRef = useRef(onMessage);
  const onPresenceRef = useRef(onPresence);
  const onTypingRef = useRef(onTyping);
  
  // Update refs when callbacks change
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);
  
  useEffect(() => {
    onPresenceRef.current = onPresence;
  }, [onPresence]);
  
  useEffect(() => {
    onTypingRef.current = onTyping;
  }, [onTyping]);

  // Build WebSocket URL - stable, only depends on conversationId and token
  const getWsUrl = useCallback(() => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsBase = baseUrl.replace(/^http/, 'ws');
    return `${wsBase}/api/ws/chat/${conversationId}?token=${token}`;
  }, [conversationId, token]);

  // Disconnect function - stable
  const disconnect = useCallback(() => {
    connectingRef.current = false;
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnected');
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // Connect function - uses refs for callbacks, stable dependencies
  const connect = useCallback(() => {
    // Prevent multiple simultaneous connections
    if (!conversationId || !token) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    if (wsRef.current?.readyState === WebSocket.CONNECTING) return;
    if (connectingRef.current) return;
    
    connectingRef.current = true;

    try {
      const ws = new WebSocket(getWsUrl());
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) {
          ws.close();
          return;
        }
        connectingRef.current = false;
        console.log('[WS] Connected to conversation:', conversationId);
        setIsConnected(true);

        // Start ping interval
        if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, WS_PING_INTERVAL);
      };

      ws.onmessage = (event) => {
        if (!mountedRef.current) return;
        try {
          const data = JSON.parse(event.data);
          
          switch (data.type) {
            case 'connected':
              setOnlineUsers(data.online_users || []);
              break;
            
            case 'message':
              if (onMessageRef.current) onMessageRef.current(data.message);
              break;
            
            case 'presence':
              if (data.is_online) {
                setOnlineUsers(prev => [...new Set([...prev, data.user_id])]);
              } else {
                setOnlineUsers(prev => prev.filter(id => id !== data.user_id));
              }
              if (onPresenceRef.current) onPresenceRef.current(data.user_id, data.is_online);
              break;
            
            case 'typing':
              if (onTypingRef.current) onTypingRef.current(data.user_id, data.is_typing);
              break;
            
            case 'read_receipt':
              break;
            
            case 'pong':
              break;
            
            default:
              console.log('[WS] Unknown message type:', data.type);
          }
        } catch (e) {
          console.error('[WS] Failed to parse message:', e);
        }
      };

      ws.onclose = (event) => {
        connectingRef.current = false;
        if (!mountedRef.current) return;
        
        console.log('[WS] Connection closed:', event.code, event.reason);
        setIsConnected(false);
        wsRef.current = null;

        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        // Auto-reconnect if not a normal closure or auth error
        if (event.code !== 1000 && event.code !== 4001 && event.code !== 4003) {
          if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = setTimeout(() => {
            if (mountedRef.current && !connectingRef.current) {
              console.log('[WS] Attempting to reconnect...');
              connect();
            }
          }, WS_RECONNECT_DELAY);
        }
      };

      ws.onerror = (error) => {
        console.error('[WS] Error:', error);
        connectingRef.current = false;
      };

    } catch (error) {
      console.error('[WS] Failed to create WebSocket:', error);
      connectingRef.current = false;
    }
  }, [conversationId, token, getWsUrl]); // Only stable dependencies

  // Send a message
  const sendMessage = useCallback((content) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'message',
        content
      }));
      return true;
    }
    return false;
  }, []);

  // Send typing indicator
  const sendTyping = useCallback((isTyping) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'typing',
        is_typing: isTyping
      }));
    }
  }, []);

  // Send read receipt
  const sendReadReceipt = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'read'
      }));
    }
  }, []);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    mountedRef.current = true;
    connectingRef.current = false;
    
    // Small delay to avoid React 18 StrictMode double-mount issues
    const timeoutId = setTimeout(() => {
      if (mountedRef.current) {
        connect();
      }
    }, 100);

    return () => {
      mountedRef.current = false;
      clearTimeout(timeoutId);
      disconnect();
    };
  }, [connect, disconnect]);

  // Check if a specific user is online
  const isUserOnline = useCallback((userId) => {
    return onlineUsers.includes(userId);
  }, [onlineUsers]);

  return {
    isConnected,
    onlineUsers,
    sendMessage,
    sendTyping,
    sendReadReceipt,
    isUserOnline,
    reconnect: connect
  };
};

export default useWebSocket;
